import { MapPin, Clock, DollarSign, SearchX } from 'lucide-react';

import { currencyFormat, formatMinutesToHours } from '@/helpers/stringHelpers';

import OfferingCategoryBadge from '@/app/offeringCategory/OfferingCategoryBadge';
import { Separator } from '@/components/ui/separator';
import { Empty, EmptyIcon, EmptyTitle } from '@/components/ui/empty';

import SelectOfferingSession from '../SelectOfferingSession';

import { useOfferingObject } from '@/data/offering';
import {
  useOfferingSessions,
  useOfferingSessionStructure
} from '@/data/offeringSession';
import { useActivityObject } from '@/data/activity';

const OfferingSessions = ({
  offeringId,
  selectedOfferingSessions,
  setSelectedOfferingSessions
}) => {
  const offering = useOfferingObject(offeringId);
  const offeringSessions = useOfferingSessions(offeringId);
  const offeringSessionStructure = useOfferingSessionStructure(offeringId);
  const activity = useActivityObject(offering.activityId);

  return (
    <div>
      <div className="flex items-center gap-4">
        <h1>{offering.name}</h1>
        <OfferingCategoryBadge offeringCategoryId={offering.categoryId} />
      </div>
      <span className="text-sm text-muted-foreground mb-4 block">
        {activity.title}
      </span>
      <div className="grid grid-cols-2 text-sm mb-4">
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span>{formatMinutesToHours(offering.length)} hours</span>
          </div>
          <div className="flex items-center space-x-2">
            <DollarSign className="w-4 h-4 text-muted-foreground" />
            <span>
              {currencyFormat(offering.subtotal).slice(1)} {offering.currency}{' '}
              /ea
            </span>
          </div>
        </div>
        <div className="space-y-1">
          {offering.location && (
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span>{offering.location.name}</span>
            </div>
          )}
        </div>
      </div>
      <Separator className="my-4" />
      <h3 className="mb-2">Time slots</h3>
      {offeringSessionStructure.data.today.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 font-medium">Today</h4>
          <div className="grid grid-cols-2 gap-2">
            {offeringSessionStructure.data.today.map((offeringSessionId) => (
              <SelectOfferingSession
                key={offeringSessionId}
                offeringId={offeringId}
                offeringSessionId={offeringSessionId}
                selectedOfferingSessions={selectedOfferingSessions}
                setSelectedOfferingSessions={setSelectedOfferingSessions}
              />
            ))}
          </div>
        </div>
      )}
      {offeringSessionStructure.data.next7Days.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 font-medium">This Week</h4>
          <div className="grid grid-cols-2 gap-2">
            {offeringSessionStructure.data.next7Days.map(
              (offeringSessionId) => (
                <SelectOfferingSession
                  key={offeringSessionId}
                  offeringId={offeringId}
                  offeringSessionId={offeringSessionId}
                  selectedOfferingSessions={selectedOfferingSessions}
                  setSelectedOfferingSessions={setSelectedOfferingSessions}
                />
              )
            )}
          </div>
        </div>
      )}
      {offeringSessionStructure.data.next30Days.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 font-medium">This Month</h4>
          <div className="grid grid-cols-2 gap-2">
            {offeringSessionStructure.data.next30Days.map(
              (offeringSessionId) => (
                <SelectOfferingSession
                  key={offeringSessionId}
                  offeringId={offeringId}
                  offeringSessionId={offeringSessionId}
                  selectedOfferingSessions={selectedOfferingSessions}
                  setSelectedOfferingSessions={setSelectedOfferingSessions}
                />
              )
            )}
          </div>
        </div>
      )}

      {offeringSessions.data.query.length === 0 && (
        <Empty>
          <EmptyIcon>
            <SearchX />
          </EmptyIcon>
          <EmptyTitle>No Time Slots Available</EmptyTitle>
        </Empty>
      )}
    </div>
  );
};

export default OfferingSessions;
